import { toClassificationDialogData } from '@classification-testing/adapters/classificationResultAdapter';
import type { ClassificationResult } from '@classification-testing/domain';
import { ClassificationType, ContentType } from '@classification-testing/domain';
import { ClassificationDialog, TimestampDisplay } from '@core/components';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import { getContentDisplayText } from '@core/utils';
import { Card, Text, contentBlurStyles } from '@soteria/design-system-ve';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { ClassificationCardControls } from './ClassificationCardControls';
import * as styles from './ClassificationResultItem.css';
import { MetadataSection } from './MetadataSection';

interface ClassificationResultItemProps {
  result: ClassificationResult;
  _classifiedText: string;
  _promptVersion: string;
}

// Helper function to determine safety status
function getSafetyStatus(result: ClassificationResult) {
  const isSafe = result.classificationType === ClassificationType.SAFE_CONTENT;
  const primaryConcern = result.labels?.length > 0 ? result.labels.reduce((prev, current) => (prev.severity > current.severity ? prev : current)) : null;

  return { isSafe, primaryConcern };
}

// Helper function to get content preview
function getContentInfo(result: ClassificationResult, isSafe: boolean) {
  const content = result.text;
  const preview = content.length > 150 ? `${content.substring(0, 150)}...` : getContentDisplayText(content, result.contentUrl);
  const shouldBlurText = !isSafe && result.text;

  return { preview, shouldBlurText };
}

// Content preview component
function ContentPreview({ preview, shouldBlurText, isContentBlurred }: { preview: string; shouldBlurText: string | false; isContentBlurred: boolean }) {
  if (!preview) return null;

  const applyBlur = shouldBlurText && isContentBlurred;

  return (
    <div className={styles.contentPreviewSection}>
      <Text className={`${styles.previewText} ${applyBlur ? contentBlurStyles.blurredText : ''}`}>{preview}</Text>
    </div>
  );
}

function ImageThumbnail({ imageUrl, altText, isSafe, isContentBlurred }: { imageUrl: string; altText: string; isSafe: boolean; isContentBlurred: boolean }) {
  const [imageError, setImageError] = useState(false);

  if (imageError) {
    return (
      <div aria-label={altText} className={styles.thumbnailContainer} role="img">
        <Text className={styles.thumbnailErrorText}>Image unavailable</Text>
      </div>
    );
  }

  const applyBlur = !isSafe && isContentBlurred;

  return (
    <div className={styles.thumbnailContainer}>
      <img
        alt={altText}
        className={`${styles.thumbnailImage} ${applyBlur ? contentBlurStyles.blurredThumbnail : ''}`}
        src={imageUrl}
        onError={() => setImageError(true)}
      />
    </div>
  );
}

// Helper component to reduce main component complexity
function ClassificationCardContent({
  result,
  isSafe,
  preview,
  shouldBlurText,
  isContentBlurred,
  onToggleBlur,
  mediaServiceUrl,
}: {
  result: ClassificationResult;
  isSafe: boolean;
  preview: string;
  shouldBlurText: string | false;
  isContentBlurred: boolean;
  onToggleBlur: (e: MouseEvent) => void;
  mediaServiceUrl?: string;
}) {
  const hasImageUrl = result.contentType === ContentType.IMAGE && result.contentUrl;

  return (
    <div
      className={`${styles.contentSummary} ${hasImageUrl ? styles.contentSummaryWithImage : ''} ${isSafe ? styles.safeContentBorder : styles.unsafeContentBorder}`}
    >
      {/* Image thumbnail column (left side) */}
      {hasImageUrl && (
        <ImageThumbnail
          altText={`Classified image from ${result.timestamp}`}
          imageUrl={result.contentUrl || ''}
          isContentBlurred={isContentBlurred}
          isSafe={isSafe}
        />
      )}

      {/* Content column (right side or full width if no image) */}
      <div className={hasImageUrl ? styles.contentColumn : ''}>
        <ContentPreview isContentBlurred={isContentBlurred} preview={preview} shouldBlurText={shouldBlurText} />
        {/* First metadata row: badges on left, audio player and blur toggle on right */}
        <div className={styles.metadataRow}>
          <div className={styles.metadataLeftSection}>
            <MetadataSection result={result} />
          </div>
          <ClassificationCardControls
            isContentBlurred={isContentBlurred}
            isSafe={isSafe}
            mediaServiceUrl={mediaServiceUrl}
            result={result}
            onToggleBlur={onToggleBlur}
          />
        </div>
        {/* Timestamp on separate line */}
        <div className={styles.metadataRow}>
          <div className={styles.metadataItem} />
          <TimestampDisplay className={styles.timestamp} timestamp={result.timestamp} />
        </div>
      </div>
    </div>
  );
}

export function ClassificationResultItem({ result }: ClassificationResultItemProps) {
  const [showDialog, setShowDialog] = useState(false);
  const { mediaServiceUrl } = useSystemConfig();
  const { isSafe } = getSafetyStatus(result);
  const [isContentBlurred, setIsContentBlurred] = useState(!isSafe);
  const { preview, shouldBlurText } = getContentInfo(result, isSafe);
  const dialogData = useMemo(() => toClassificationDialogData(result), [result]);

  const handleCardClick = () => {
    setShowDialog(true);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowDialog(true);
    }
  };

  const handleToggleBlur = (e: MouseEvent) => {
    e.stopPropagation(); // Prevent opening the dialog
    setIsContentBlurred(prev => !prev);
  };

  const handleDialogClose = useCallback(() => setShowDialog(false), []);
  const handleDialogToggleBlur = useCallback(() => setIsContentBlurred(prev => !prev), []);

  return (
    <>
      <Card className={styles.classificationCard} tabIndex={0} onClick={handleCardClick} onKeyDown={handleKeyDown}>
        <ClassificationCardContent
          isContentBlurred={isContentBlurred}
          isSafe={isSafe}
          mediaServiceUrl={mediaServiceUrl}
          preview={preview}
          result={result}
          shouldBlurText={shouldBlurText}
          onToggleBlur={handleToggleBlur}
        />
      </Card>

      <ClassificationDialog
        data={dialogData}
        isContentBlurred={isContentBlurred}
        isOpen={showDialog}
        onClose={handleDialogClose}
        onToggleBlur={handleDialogToggleBlur}
      />
    </>
  );
}
